import axios from 'axios';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { loginRequest } from '../components/auth/authConfig';
import {
    getAnonymousHeaders,
    getOrCreateSession as getOrCreateAnonymousSession,
} from '../services/anonymousSessionService';

const isLocalhost = (baseUrl) => {
    return baseUrl?.includes('localhost') || baseUrl?.includes('127.0.0.1');
};

// Error types for better error handling
export const AuthError = {
    NO_ACCOUNT: 'NO_ACCOUNT',
    TOKEN_EXPIRED: 'TOKEN_EXPIRED',
    REFRESH_FAILED: 'REFRESH_FAILED',
    NETWORK_ERROR: 'NETWORK_ERROR'
};

const getAccessToken = async (msalInstance) => {
    const accounts = msalInstance.getAllAccounts();
    
    if (!accounts || accounts.length === 0) {
        const error = new Error('No active account found. Please sign in.');
        error.type = AuthError.NO_ACCOUNT;
        throw error;
    }

    const account = accounts[0];
    
    try {
        const response = await msalInstance.acquireTokenSilent({
            ...loginRequest,
            account: account
        });
        // Persist the idToken so logoutRedirect can pass idTokenHint later,
        // even after MSAL has removed the account from its local cache.
        if (response.idToken) {
            sessionStorage.setItem('msal.lastIdToken', response.idToken);
        }
        return response.accessToken;
    } catch (error) {
        const needsInteraction =
            error instanceof InteractionRequiredAuthError ||
            error.errorCode === 'interaction_required' ||
            error.errorCode === 'consent_required' ||
            error.errorCode === 'login_required' ||
            error.errorCode === 'invalid_grant';

        if (needsInteraction) {
            // Stale cache: account present but no server session (e.g. AADSTS160021). Drop
            // the account so useIsAuthenticated() is false and the UI can prompt sign-in.
            try {
                msalInstance.removeAccount(account);
                const remaining = msalInstance.getAllAccounts();
                msalInstance.setActiveAccount(remaining[0] ?? null);
            } catch {
                /* ignore cache clear errors */
            }
            const authError = new Error('Authentication required. Please sign in again.');
            authError.type = AuthError.TOKEN_EXPIRED;
            authError.originalError = error;
            throw authError;
        }

        if (process.env.NODE_ENV === 'development') {
            console.error('Error acquiring token silently:', error);
        }

        // Handle different types of token acquisition errors
        if (error.errorCode === 'network_error' ||
                   error.errorCode === 'endpoints_resolution_error') {
            const networkError = new Error('Network error while refreshing token. Please check your connection.');
            networkError.type = AuthError.NETWORK_ERROR;
            networkError.originalError = error;
            throw networkError;
        } else {
            // Other token refresh failures
            const refreshError = new Error('Failed to refresh authentication token.');
            refreshError.type = AuthError.REFRESH_FAILED;
            refreshError.originalError = error;
            throw refreshError;
        }
    }
};

export const createApiCall = (baseUrl, msalInstance) => {
    return async (endpoint, data, options = {}) => {
        let url = `${baseUrl}${endpoint}`;
        const headers = {
            'Content-Type': 'application/json',
            ...options.headers
        };

        // Handle query parameters
        if (options.params) {
            const params = new URLSearchParams();
            Object.entries(options.params).forEach(([key, value]) => {
                params.append(key, value);
            });
            const separator = url.includes('?') ? '&' : '?';
            url = `${url}${separator}${params.toString()}`;
        }

        // Endpoints that also work with an anonymous trial session token.
        // These are the bits of the flow anonymous visitors need to navigate
        // to (and through) blog generation before seeing the signup modal.
        // All other endpoints still require an MSAL bearer token.
        const anonymousAllowedEndpoints = [
            '/openai-h1',
            '/google-trend',
            '/anonymous/session',
        ];
        const isAnonymousAllowed = anonymousAllowedEndpoints.some(ep => endpoint === ep || endpoint.startsWith(`${ep}?`));

        const hasMsalAccount = Boolean(msalInstance?.getAllAccounts?.().length);

        try {
            if (hasMsalAccount || options.forceAuth || !isAnonymousAllowed) {
                if (!msalInstance) {
                    const error = new Error('MSAL instance not available');
                    error.type = AuthError.NO_ACCOUNT;
                    throw error;
                }

                const token = await getAccessToken(msalInstance);
                headers['Authorization'] = `Bearer ${token}`;
            } else {
                // No MSAL account — fall back to the anonymous session token
                // so endpoints flagged as anonymousAllowed can serve trial
                // visitors during their 2-blog free run.
                let anonHeaders = getAnonymousHeaders();
                if (!anonHeaders) {
                    try {
                        await getOrCreateAnonymousSession();
                        anonHeaders = getAnonymousHeaders();
                    } catch (anonErr) {
                        const error = new Error(anonErr?.message || 'Could not start anonymous session');
                        error.type = AuthError.NO_ACCOUNT;
                        error.originalError = anonErr;
                        throw error;
                    }
                }
                if (anonHeaders) {
                    Object.assign(headers, anonHeaders);
                } else {
                    const error = new Error('Anonymous session unavailable');
                    error.type = AuthError.NO_ACCOUNT;
                    throw error;
                }
            }

            if (!isLocalhost(baseUrl)) {
                const separator = url.includes('?') ? '&' : '?';
                url = `${url}${separator}code=${process.env.REACT_APP_API_CODE}`;
            }
        } catch (error) {
            if (error.type !== AuthError.TOKEN_EXPIRED && error.type !== AuthError.NO_ACCOUNT) {
                console.error('Failed to get access token:', error);
            }
            throw error;
        }

        try {
            const response = await axios({
                method: options.method || 'POST',
                url,
                data,
                headers,
                timeout: 30000 // 30 second timeout
            });
            return response.data;
        } catch (error) {
            console.error('API call failed:', error);
            
            // Enhanced error handling for API responses
            if (error.response) {
                // Server responded with error status
                const { status, data } = error.response;
                
                if (status === 401) {
                    const authError = new Error('Authentication failed. Please sign in again.');
                    authError.type = AuthError.TOKEN_EXPIRED;
                    authError.originalError = error;
                    throw authError;
                } else if (status === 403) {
                    const permissionError = new Error('Access denied. You may not have permission for this action.');
                    permissionError.type = 'PERMISSION_DENIED';
                    permissionError.originalError = error;
                    throw permissionError;
                } else {
                    const apiError = new Error(data?.message || `API error: ${status}`);
                    apiError.type = 'API_ERROR';
                    apiError.status = status;
                    apiError.originalError = error;
                    throw apiError;
                }
            } else if (error.request) {
                // Network error
                const networkError = new Error('Network error. Please check your connection and try again.');
                networkError.type = AuthError.NETWORK_ERROR;
                networkError.originalError = error;
                throw networkError;
            } else {
                throw error;
            }
        }
    };
};

// Default export that will be configured with MSAL instance
export let apiCall = null;

// Function to initialize the API call with MSAL instance
export const initializeApiCall = (msalInstance) => {
    apiCall = createApiCall(process.env.REACT_APP_API_BASE_URL, msalInstance);
}; 